// src/services/notificationService.js
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

const MESSAGE_REQUESTS_COLLECTION = 'payback-message-requests';

// Template types
export const MessageTemplate = {
  INITIAL_NOTICE: 'INITIAL_NOTICE',
  REMINDER: 'REMINDER',
  FINAL_WARNING: 'FINAL_WARNING',
  SETTLEMENT_REQUEST: 'SETTLEMENT_REQUEST',
};

// Generate message content based on template
export const generateMessageContent = (report, templateType = MessageTemplate.INITIAL_NOTICE) => {
  const templates = {
    [MessageTemplate.INITIAL_NOTICE]: {
      subject: `PAYBACK Notice: Outstanding Debt of $${report.amount?.toLocaleString()}`,
      body: `Dear ${report.borrowerName},

This is an official notice from PAYBACK regarding an outstanding debt claim filed against you.

CLAIM DETAILS:
- Amount: $${report.amount?.toLocaleString()}
- Lender: ${report.lenderName}
- Due Date: ${report.dueDate}
- Reference: ${report.id?.substring(0, 8).toUpperCase()}

LENDER'S STATEMENT:
"${report.description}"

⚠️ IMPORTANT: You have 7 DAYS to respond before this report becomes PUBLIC on the PAYBACK registry.

YOUR OPTIONS:
1. PAY the debt and request removal
2. DISPUTE the claim with evidence
3. NEGOTIATE a settlement

Respond here: ${getResponseUrl(report.id)}

Failure to respond will result in this claim being permanently visible to future lenders and credit checks.

- PAYBACK Platform
Community Trust Registry`,
    },
    
    [MessageTemplate.REMINDER]: {
      subject: `REMINDER: Debt Claim of $${report.amount?.toLocaleString()} - Action Required`,
      body: `Dear ${report.borrowerName},

This is a REMINDER regarding the outstanding debt claim against you.

- Amount: $${report.amount?.toLocaleString()}
- Reference: ${report.id?.substring(0, 8).toUpperCase()}
- Days Remaining: ${calculateDaysRemaining(report.createdDate)}

The lender is waiting for your response. Please take action to avoid public listing.

Respond: ${getResponseUrl(report.id)}

- PAYBACK Platform`,
    },
    
    [MessageTemplate.FINAL_WARNING]: {
      subject: `⚠️ FINAL WARNING: Public Listing Imminent - $${report.amount?.toLocaleString()}`,
      body: `Dear ${report.borrowerName},

⚠️ FINAL WARNING ⚠️

Your grace period expires in 24-48 HOURS.

- Claim Amount: $${report.amount?.toLocaleString()}
- Reference: ${report.id?.substring(0, 8).toUpperCase()}

After this period, the debt report will be PERMANENTLY VISIBLE on our public registry and may affect your ability to obtain credit from community lenders.

Take immediate action: ${getResponseUrl(report.id)}

- PAYBACK Platform`,
    },
    
    [MessageTemplate.SETTLEMENT_REQUEST]: {
      subject: `Settlement Opportunity - $${report.amount?.toLocaleString()} Debt`,
      body: `Dear ${report.borrowerName},

The lender has indicated willingness to discuss a settlement for your outstanding debt.

- Original Amount: $${report.amount?.toLocaleString()}
- Reference: ${report.id?.substring(0, 8).toUpperCase()}

This may be an opportunity to resolve this matter favorably. Please respond to discuss terms.

Respond: ${getResponseUrl(report.id)}

- PAYBACK Platform`,
    },
  };

  return templates[templateType] || templates[MessageTemplate.INITIAL_NOTICE];
};

// Generate share links for different platforms
export const generateShareLinks = (report, templateType = MessageTemplate.INITIAL_NOTICE) => {
  const content = generateMessageContent(report, templateType);
  const phone = formatPhoneForWhatsApp(report.borrowerMobile);
  
  // WhatsApp link
  const waMessage = encodeURIComponent(content.body);
  const waLink = `https://wa.me/${phone}?text=${waMessage}`;
  
  // Email link
  const mailSubject = encodeURIComponent(content.subject);
  const mailBody = encodeURIComponent(content.body);
  const mailLink = report.borrowerEmail 
    ? `mailto:${report.borrowerEmail}?subject=${mailSubject}&body=${mailBody}`
    : null;
  
  // SMS link (for mobile)
  const smsBody = encodeURIComponent(content.body.substring(0, 160) + '...');
  const smsLink = `sms:${report.borrowerMobile}?body=${smsBody}`;
  
  return {
    waLink,
    mailLink,
    smsLink,
    smsContent: content.body,
    subject: content.subject,
    rawContent: content,
    phone,
  };
};

// Format phone number for WhatsApp
const formatPhoneForWhatsApp = (phone) => {
  if (!phone) return '';
  // Remove all non-numeric characters
  let cleaned = phone.replace(/\D/g, '');
  // If starts with 0, assume South Africa and replace with 27
  if (cleaned.startsWith('0')) {
    cleaned = '27' + cleaned.substring(1);
  }
  // If no country code, assume it needs one
  if (cleaned.length === 9) {
    cleaned = '27' + cleaned;
  }
  return cleaned;
};

// Get response URL
const getResponseUrl = (reportId) => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/#/manage/${reportId}`;
};

// Calculate days remaining in grace period
const calculateDaysRemaining = (createdDate) => {
  if (!createdDate) return 7;
  const created = createdDate.seconds 
    ? new Date(createdDate.seconds * 1000) 
    : new Date(createdDate);
  const now = new Date();
  const diffTime = 7 * 24 * 60 * 60 * 1000 - (now - created);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
};

// Request admin to send message
export const requestAdminMessage = async (report, templateType, senderId, senderName, channels = ['whatsapp', 'email']) => {
  try {
    const content = generateMessageContent(report, templateType);
    
    const request = {
      reportId: report.id,
      borrowerName: report.borrowerName,
      borrowerMobile: report.borrowerMobile,
      borrowerEmail: report.borrowerEmail || null,
      lenderId: senderId,
      lenderName: senderName,
      templateType,
      messageSubject: content.subject,
      messageBody: content.body,
      channels,
      status: 'PENDING',
      createdAt: serverTimestamp(),
      amount: report.amount,
    };
    
    const docRef = await addDoc(collection(db, MESSAGE_REQUESTS_COLLECTION), request);
    return { success: true, requestId: docRef.id };
  } catch (error) {
    console.error('Error creating message request:', error);
    throw error;
  }
};

// Log message sent by lender
export const logMessageSent = async (report, templateType, senderId, channel) => {
  try {
    await addDoc(collection(db, 'payback-message-logs'), {
      reportId: report.id,
      borrowerMobile: report.borrowerMobile,
      borrowerEmail: report.borrowerEmail || null,
      senderId,
      templateType,
      channel,
      sentAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    console.error('Error logging message:', error);
    throw error;
  }
};